/**
 * Session Expiry Warning Hook
 *
 * Monitors session expiry and shows a warning toast 5 minutes before expiry,
 * giving users the option to manually extend their session. This provides
 * a proactive UX that competitors (pump.fun, bonk.fun) don't have.
 *
 * Works alongside useSessionManager which handles silent auto-refresh.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';

// Show warning 5 minutes before expiry
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;
// Check session every 30 seconds
const CHECK_INTERVAL_MS = 30 * 1000;
// Auto-dismiss toast after 30 seconds if no action
const TOAST_AUTO_DISMISS_MS = 30 * 1000;

export function useSessionExpiryWarning() {
  const { isAuthenticated } = useTurnkeyWallet();
  const [isWarningShown, setIsWarningShown] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);

  // Refs to track state without causing re-renders
  const warningDismissedRef = useRef(false);
  const toastIdRef = useRef<string | number | null>(null);
  const lastExpiryRef = useRef<number | null>(null);

  /**
   * Refresh the session manually when user clicks "Stay Signed In"
   */
  const refreshSession = useCallback(async () => {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) throw error;

      // Dismiss warning toast
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      setIsWarningShown(false);
      warningDismissedRef.current = false;

      // Show success feedback
      toast.success('Session extended', {
        description: 'You can continue trading.',
        duration: 3000,
      });
    } catch (error) {
      console.error('[SessionExpiryWarning] Failed to refresh:', error);
      toast.error('Failed to extend session', {
        description: 'Please sign in again.',
        duration: 5000,
      });
    }
  }, []);

  /**
   * Dismiss the warning (user accepts the risk of expiry)
   */
  const dismissWarning = useCallback(() => {
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
    setIsWarningShown(false);
    warningDismissedRef.current = true;
  }, []);

  /**
   * Show the warning toast with countdown
   */
  const showWarningToast = useCallback(
    (msRemaining: number) => {
      const minutes = Math.ceil(msRemaining / 60000);

      // Don't show duplicate toasts
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }

      toastIdRef.current = toast.warning('Session expiring soon', {
        description: `Your session expires in ${minutes} minute${minutes !== 1 ? 's' : ''}`,
        duration: TOAST_AUTO_DISMISS_MS,
        action: {
          label: 'Stay Signed In',
          onClick: refreshSession,
        },
        cancel: {
          label: 'Dismiss',
          onClick: dismissWarning,
        },
        onDismiss: () => {
          // When toast auto-dismisses or is dismissed, mark as dismissed
          setIsWarningShown(false);
          warningDismissedRef.current = true;
        },
      });

      setIsWarningShown(true);
    },
    [refreshSession, dismissWarning]
  );

  useEffect(() => {
    // Only run when user is authenticated
    if (!isAuthenticated) {
      // Reset state when logged out
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      setIsWarningShown(false);
      warningDismissedRef.current = false;
      lastExpiryRef.current = null;
      setTimeUntilExpiry(null);
      return;
    }

    /**
     * Check session expiry and show warning if needed
     */
    const checkSessionExpiry = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.expires_at) {
          setTimeUntilExpiry(null);
          return;
        }

        const expiresAtMs = session.expires_at * 1000;
        const now = Date.now();
        const msUntilExpiry = expiresAtMs - now;

        setTimeUntilExpiry(msUntilExpiry);

        // Detect if session was refreshed (expiry time changed)
        if (lastExpiryRef.current && expiresAtMs > lastExpiryRef.current) {
          // Session was refreshed, reset warning state
          if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
            toastIdRef.current = null;
          }
          setIsWarningShown(false);
          warningDismissedRef.current = false;
        }
        lastExpiryRef.current = expiresAtMs;

        // Show warning if within threshold and not already shown/dismissed
        if (
          msUntilExpiry > 0 &&
          msUntilExpiry <= WARNING_THRESHOLD_MS &&
          !isWarningShown &&
          !warningDismissedRef.current
        ) {
          showWarningToast(msUntilExpiry);
        }
      } catch (err) {
        // Silent fail for background checks
        console.debug('[SessionExpiryWarning] Check failed:', err);
      }
    };

    // Initial check
    checkSessionExpiry();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkSessionExpiry, CHECK_INTERVAL_MS);

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED') {
        // Session was refreshed (possibly by background auto-refresh)
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }
        setIsWarningShown(false);
        warningDismissedRef.current = false;
      }

      if (event === 'SIGNED_OUT') {
        // Clear everything on sign out
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }
        setIsWarningShown(false);
        warningDismissedRef.current = false;
        lastExpiryRef.current = null;
        setTimeUntilExpiry(null);
      }
    });

    // Cleanup
    return () => {
      clearInterval(intervalId);
      subscription.unsubscribe();
    };
  }, [isAuthenticated, isWarningShown, showWarningToast]);

  return {
    timeUntilExpiry,
    isWarningShown,
    refreshSession,
    dismissWarning,
  };
}
